import { Building2, CreditCard, Heart } from 'lucide-react'
import { useTranslation } from 'react-i18next'

import { CARD_TYPE } from '../../admin/modules/GestionCartesAccesEtBienEtre/shared/constants'

export const AMICALE_MOTEUR_TAB = {
  HOTEL: 'hotel',
  CARTE_ACCES: CARD_TYPE.ACCES,
  BIEN_ETRE: CARD_TYPE.BIEN_ETRE,
}

const TAB_META = {
  [AMICALE_MOTEUR_TAB.HOTEL]: {
    labelKey: 'amicales.products.hotel',
    Icon: Building2,
    iconBg: 'bg-sky-100',
    iconColor: 'text-sky-600',
    activeRing: 'ring-sky-200',
    activeBar: 'bg-sky-500',
  },
  [AMICALE_MOTEUR_TAB.CARTE_ACCES]: {
    labelKey: 'amicales.products.accessCard',
    Icon: CreditCard,
    iconBg: 'bg-violet-100',
    iconColor: 'text-violet-600',
    activeRing: 'ring-violet-200',
    activeBar: 'bg-violet-500',
  },
  [AMICALE_MOTEUR_TAB.BIEN_ETRE]: {
    labelKey: 'amicales.products.wellness',
    Icon: Heart,
    iconBg: 'bg-emerald-100',
    iconColor: 'text-emerald-600',
    activeRing: 'ring-emerald-200',
    activeBar: 'bg-emerald-500',
  },
}

const buildTabIds = ({ hasCarteAcces, hasBienEtre }) => {
  const ids = [AMICALE_MOTEUR_TAB.HOTEL]
  if (hasCarteAcces) ids.push(AMICALE_MOTEUR_TAB.CARTE_ACCES)
  if (hasBienEtre) ids.push(AMICALE_MOTEUR_TAB.BIEN_ETRE)
  return ids
}

const AmicaleMoteurTab = ({ tabId, active, onSelect }) => {
  const { t } = useTranslation()
  const meta = TAB_META[tabId]
  const { labelKey, Icon, iconBg, iconColor, activeRing, activeBar } = meta || {}
  if (!meta) return null

  const label = t(labelKey)

  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      aria-label={label}
      onClick={() => onSelect(tabId)}
      className={[
        'group relative flex min-h-[44px] flex-1 flex-col items-center justify-center gap-1.5 rounded-xl px-3 py-2.5 transition-all duration-200',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-400 focus-visible:ring-offset-2',
        active
          ? `bg-white shadow-md ring-1 ${activeRing}`
          : 'bg-transparent hover:bg-white/70',
      ].join(' ')}
    >
      <span
        className={[
          'flex h-9 w-9 items-center justify-center rounded-xl transition-transform duration-200',
          iconBg,
          active ? 'scale-110 shadow-sm' : 'scale-100 group-hover:scale-105',
        ].join(' ')}
        aria-hidden
      >
        <Icon className={`h-[1.125rem] w-[1.125rem] ${iconColor}`} strokeWidth={2.25} />
      </span>
      <span
        className={[
          'text-center text-xs font-semibold leading-tight sm:text-[0.8125rem]',
          active ? 'text-gray-900' : 'text-gray-500 group-hover:text-gray-700',
        ].join(' ')}
      >
        {label}
      </span>
      <span
        className={[
          'absolute bottom-0 left-1/2 h-0.5 -translate-x-1/2 rounded-full transition-all duration-200',
          active ? `w-8 ${activeBar}` : 'w-0 bg-transparent',
        ].join(' ')}
        aria-hidden
      />
    </button>
  )
}

const AmicaleMoteurTabs = ({ activeTab, onChange, hasCarteAcces, hasBienEtre }) => {
  const { t } = useTranslation()
  const tabIds = buildTabIds({ hasCarteAcces, hasBienEtre })

  if (tabIds.length <= 1) return null

  return (
    <div className="mb-6 flex justify-center px-4 md:px-0">
      <div
        role="tablist"
        aria-label={t('amicales.products.reservationTypeAria')}
        className="grid w-full max-w-3xl gap-1 rounded-2xl border border-gray-100 bg-gradient-to-b from-slate-50 to-white p-1.5 shadow-sm"
        style={{ gridTemplateColumns: `repeat(${tabIds.length}, minmax(0, 1fr))` }}
      >
        {tabIds.map((tabId) => (
          <AmicaleMoteurTab
            key={tabId}
            tabId={tabId}
            active={activeTab === tabId}
            onSelect={onChange}
          />
        ))}
      </div>
    </div>
  )
}

export default AmicaleMoteurTabs
